import { Machine, assign } from "xstate";
import { uuid } from "uuidv4";

import { SendMessage } from "../utils/Chatbot";

const invokeFetchResponse = async (
  context: ChatbotContext
): Promise<string> => {
  const message = context.messages[context.messages.length - 1].message;
  const result = await SendMessage(message, context.id);

  // Example result value: ["Yes", "What else can I do for you"]

  // Add any Firebase logging here
  // Example:
  // await firebase.push(message, result)

  return result;
};

interface ChatbotSchema {
  states: {
    idle: {};
    loading: {};
  };
}

type ChatbotEvent = { type: "CHANGE"; message: string } | { type: "SEND" };

export interface Message {
  sender: "user" | "bot";
  message: string;
}

interface ChatbotContext {
  id: string;
  messages: Message[];
  message: string;
}

const chatbotMachine = Machine<ChatbotContext, ChatbotSchema, ChatbotEvent>({
  id: "chatbot",
  initial: "idle",
  context: {
    id: uuid(),
    messages: [],
    message: "",
  },
  states: {
    idle: {
      on: {
        CHANGE: {
          actions: assign({
            message: (context, event) => event.message,
          }),
        },
        SEND: {
          target: "loading",
          cond: (context, event) => context.message.length > 0,
          actions: assign({
            messages: (context, event) => [
              ...context.messages,
              { sender: "user", message: context.message },
            ],
            message: (context, event) => "",
          }),
        },
      },
    },
    loading: {
      invoke: {
        id: "fetch-response",
        src: invokeFetchResponse,
        onDone: {
          target: "idle",
          actions: assign({
            messages: (context, event) => {
              return [
                ...context.messages,
                ...event.data.map((e: Message) => ({
                  sender: "bot",
                  message: e,
                })),
              ];
            },
          }),
        },
        onError: {
          target: "idle",
          actions: assign({
            messages: (context, event) => [
              ...context.messages,
              {
                sender: "bot",
                message:
                  "Sorry, something seems to have gone wrong. Try again?",
              },
            ],
          }),
        },
      },
    },
  },
});

export default chatbotMachine;
